import React from "react";
import { Flex, ThemeUICSSObject } from "theme-ui";
import {
  useCombobox,
  UseComboboxGetInputPropsOptions,
  UseComboboxGetItemPropsOptions,
  UseComboboxGetLabelPropsOptions,
  UseComboboxGetMenuPropsOptions,
} from "downshift";
import { useState, ReactNode, useEffect } from "react";
import { Icon } from "../Icons";

interface TypeaheadProps {
  /**
   * Informative label to display above the input
   */
  label: ReactNode;
  /**
   * Additional info to help the user; displayed below the input
   */
  hint?: ReactNode;
  /**
   * Error text to display. If defined, the component will appear invalid
   */
  error?: string;
  /**
   * List of items to select from
   */
  items: string[];
  /**
   * Action on change
   */
  isCashMoney?: boolean;
  onChange: (value: string) => void;
  inputStyle?: React.CSSProperties;
  labelStyle?: React.CSSProperties;
  placeholder?: string;
  autoFocus?: boolean;
}

export const Typeahead: React.FC<TypeaheadProps> = ({ items, ...props }) => {
  const [inputItems, setInputItems] = useState(items);
  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    openMenu,
    highlightedIndex,
    getItemProps,
    reset,
  } = useCombobox({
    items: inputItems,
    onInputValueChange: ({ inputValue = "" }) => {
      setInputItems(
        !inputValue
          ? items
          : items.filter((item) => {
              return item.toLowerCase().startsWith(inputValue.toLowerCase());
            })
      );
    },
    onSelectedItemChange: ({ inputValue = "" }) => {
      if (inputValue !== "") {
        props.onChange(inputValue);
      }
      reset();
    },
  });
  useEffect(() => {
    setInputItems(items);
  }, [items]);
  const [error] = useState(false);
  return (
    <Flex sx={{ position: "relative", flexFlow: "column nowrap" }}>
      <label
        {...getLabelProps({
          style: props.labelStyle,
          sx: {
            variant: "type.label",
          } as ThemeUICSSObject,
        } as UseComboboxGetLabelPropsOptions)}
      >
        {props.label}
      </label>
      <input
        {...getInputProps({
          onFocus: openMenu,
          placeholder: props.placeholder,
          style: props.inputStyle,
          sx: {
            flex: "1 0 auto",
            appearance: "none",
            boxSizing: "border-box",
            width: "100%",
            py: props.isCashMoney ? 2 : 1,
            px: 3,
            border: props.isCashMoney && "none !important",
            boxShadow: props.isCashMoney ? "0px 3px 6px #00000029" : "medium",
            borderRadius: props.isCashMoney && "7px",
            variant: "type.body",
            color: error ? "red" : "text",
            outlineColor: "transparent",
            transitionProperty: "outline-color",
            transitionDuration: "150ms",
            transitionTimingFunction: "ease-out",
            "&::placeholder": {
              color: "inherit",
            },
            "&:focus, &:active": {
              // outline: 0,
              outlineWidth: 2,
              outlineColor: "#777",
              // outlineStyle: 'solid',
            },
          } as ThemeUICSSObject,
        } as UseComboboxGetInputPropsOptions)}
      />
      <button
        {...getToggleButtonProps({
          sx: {
            variant: "buttons.base",
            backgroundColor: "transparent",
            position: "absolute",
            top: 0,
            right: 0,
            display: "inline-flex",
            justifyContent: "center",
            alignItems: "center",
            color: "inherit",
            p: 2,
            mt: 1,
            mr: 1,
          },
        } as any)}
        aria-label={"toggle menu"}
      >
        <Icon
          name="chevron-down"
          alt="Open Typeahead Icon"
          height="16px"
          width="15px"
          fill={props.isCashMoney ? "primary" : "inherit"}
          viewBox="0 0 15 8.904"
          sx={{
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease-out",
          }}
        />
      </button>
      <ul
        {...getMenuProps({
          sx: {
            maxHeight: "180px",
            display: isOpen ? "block" : "none",
            overflowY: "auto",
            width: "100%",
            margin: 0,
            background: "white",
            position: "absolute",
            zIndex: 1000,
            listStyle: "none",
            padding: 0,
            top: "100%",
            textAlign: "left",
          } as ThemeUICSSObject,
        } as UseComboboxGetMenuPropsOptions)}
      >
        {isOpen &&
          inputItems.map((item, index) => {
            const isHighlighted = highlightedIndex === index;
            return (
              <li
                key={`${item}${index}`}
                {...getItemProps({
                  item,
                  index,
                  sx: {
                    variant: "type.body",
                    backgroundColor: isHighlighted ? "#EEE" : "white",
                    color: isHighlighted ? "#000" : "text",
                    p: 2,
                    cursor: "pointer",
                  } as ThemeUICSSObject,
                } as UseComboboxGetItemPropsOptions<any>)}
              >
                {item}
              </li>
            );
          })}
      </ul>
    </Flex>
  );
};
